import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";

import v2_actionAutorizarDesconto from "../actions/v2/descontos/v2_actionAutorizarDesconto";
import { soNumeros } from "../utils/functions";

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "R$ 0,00";
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
};

/**
 * Modal de autorização de desconto (pedido ou produto).
 * Props:
 * - open, onClose
 * - escopo: "pedido" | "produto"
 * - baseValor: valor base em R$ (subtotal líquido ou preço do item)
 * - titulo?: string
 * - onAutorizado({ valorReais, autorizacao, modo, valorInformado })
 */
const ModalAutorizacaoDesconto = ({
  open,
  onClose,
  escopo = "pedido",
  baseValor = 0,
  titulo,
  onAutorizado,
}) => {
  const [modo, setModo] = useState("valor");
  const [valor, setValor] = useState("");
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const base = Math.max(0, Number(baseValor) || 0);

  useEffect(() => {
    if (!open) return;
    setModo("valor");
    setValor("");
    setCpf("");
    setPassword("");
    setMotivo("");
    setErro("");
    setLoading(false);
  }, [open]);

  const valorNum = useMemo(() => {
    const raw = String(valor).replace(",", ".");
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }, [valor]);

  const valorReais = useMemo(() => {
    if (modo === "percentual") {
      return Number(((base * valorNum) / 100).toFixed(2));
    }
    return Number(valorNum.toFixed(2));
  }, [modo, valorNum, base]);

  const tituloModal =
    titulo ||
    (escopo === "produto" ? "Desconto no produto" : "Desconto no pedido");

  const validarLocal = () => {
    if (base <= 0) {
      return "Não há valor base para aplicar desconto.";
    }
    if (!valorNum || valorNum <= 0) {
      return "Informe um valor de desconto válido.";
    }
    if (modo === "percentual" && valorNum > 100) {
      return "Percentual não pode ser maior que 100%.";
    }
    if (valorReais > base + 0.001) {
      return "Desconto não pode ser maior que o valor base.";
    }
    if (soNumeros(cpf).length !== 11) {
      return "CPF inválido.";
    }
    if (!password) {
      return "Informe a senha do autorizador.";
    }
    if (String(motivo || "").trim().length < 5) {
      return "Motivo deve ter pelo menos 5 caracteres.";
    }
    return "";
  };

  const handleConfirmar = async () => {
    const erroLocal = validarLocal();
    if (erroLocal) {
      setErro(erroLocal);
      return;
    }

    setLoading(true);
    setErro("");

    try {
      const response = await v2_actionAutorizarDesconto({
        cpf: soNumeros(cpf),
        password,
        motivo: String(motivo).trim(),
        escopo,
        modo,
        valor: valorNum,
      });

      if (response.status === 200 && response.data?.autorizado) {
        onAutorizado?.({
          valorReais,
          autorizacao: {
            ...response.data,
            valorReais,
            modo,
            valorInformado: valorNum,
            escopo,
          },
          modo,
          valorInformado: valorNum,
        });
        toast.success("Desconto autorizado com sucesso.");
        onClose?.();
        return;
      }

      setErro(response.data?.message || "Não foi possível autorizar o desconto.");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Falha ao autorizar desconto. Verifique CPF, senha e permissão.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {tituloModal}
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          disabled={loading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
            Somente <strong>Gerentes</strong>, <strong>Subgerentes</strong> ou
            usuários do <strong>escritório (backoffice)</strong> podem autorizar
            descontos. Informe o CPF e a senha de quem irá autorizar.
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary">
              Valor base
            </Typography>
            <Typography fontWeight={700}>{formatMoney(base)}</Typography>
          </Box>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={modo}
            onChange={(_, next) => {
              if (next) setModo(next);
            }}
            fullWidth
          >
            <ToggleButton value="valor">R$</ToggleButton>
            <ToggleButton value="percentual">%</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label={modo === "percentual" ? "Percentual" : "Valor do desconto"}
            value={valor}
            onChange={(e) => setValor(e.target.value.replace(/[^\d.,]/g, ""))}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {modo === "percentual" ? "%" : "R$"}
                </InputAdornment>
              ),
            }}
            helperText={
              modo === "percentual"
                ? `Equivalente a ${formatMoney(valorReais)}`
                : undefined
            }
          />

          <TextField
            label="CPF do autorizador"
            value={cpf}
            onChange={(e) => setCpf(e.target.value.replace(/\D/g, "").slice(0, 11))}
            fullWidth
            size="small"
            inputProps={{ inputMode: "numeric" }}
          />

          <TextField
            label="Senha"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            size="small"
            autoComplete="current-password"
          />

          <TextField
            label="Motivo do desconto"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Ex.: promoção da loja, ajuste comercial..."
          />

          {erro ? (
            <Typography color="error" variant="body2">
              {erro}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmar}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Autorizar e aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAutorizacaoDesconto;
